/**
 * Editor document validator — two gates before a compiled document is
 * released: (1) the document is structurally consumable by the existing
 * iVisora editor, and (2) it faithfully preserves the DesignSpec it came
 * from. Compilation fails loudly on either.
 */

const ELEMENT_TYPES = new Set(['text', 'image', 'shape', 'frame', 'sticker', 'group', 'svg']);
const FRAME_SHAPES = new Set(['rectangle', 'circle']); // frameShapes.js ids we emit

const finite = (v) => Number.isFinite(v);

/** Gate 1 — editor compatibility. */
const validateEditorCompat = (doc, errors) => {
  if (!finite(doc.canvas_width) || !finite(doc.canvas_height)) {
    errors.push('document needs numeric canvas_width/canvas_height');
  }
  if (!Array.isArray(doc.pages) || doc.pages.length === 0) {
    errors.push('document needs at least one page');
    return;
  }

  doc.pages.forEach((page) => {
    if (!page.id || !Array.isArray(page.elements)) {
      errors.push(`page "${page.id}" needs id and elements[]`);
      return;
    }
    if (typeof page.background !== 'string') errors.push(`page "${page.id}" needs a background color`);

    const ids = new Set();
    const groupIds = new Set(page.elements.filter((el) => el.type === 'group').map((el) => el.id));

    page.elements.forEach((el) => {
      if (!el.id) { errors.push('element without id'); return; }
      if (ids.has(el.id)) errors.push(`duplicate element id "${el.id}"`);
      ids.add(el.id);

      if (!ELEMENT_TYPES.has(el.type)) errors.push(`${el.id}: unknown element type "${el.type}"`);
      if (typeof el.locked !== 'boolean' || typeof el.visible !== 'boolean') {
        errors.push(`${el.id}: locked/visible must be booleans`);
      }
      if (el.parentId && !groupIds.has(el.parentId)) {
        errors.push(`${el.id}: parentId "${el.parentId}" is not a group on this page`);
      }

      if (el.type === 'group') return; // groups carry no geometry (derivedBounds)

      if (![el.x, el.y, el.width, el.height].every(finite)) {
        errors.push(`${el.id}: geometry must be finite numbers`);
      }
      if (el.type === 'text' && (typeof el.text !== 'string' || !finite(el.fontSize) || !el.fontFamily || !el.color)) {
        errors.push(`${el.id}: text element needs text/fontSize/fontFamily/color`);
      }
      if (el.type === 'shape' && (!el.shape || !el.fill)) {
        errors.push(`${el.id}: shape element needs shape kind and fill`);
      }
      if (el.type === 'frame') {
        if (!FRAME_SHAPES.has(el.shape)) errors.push(`${el.id}: frame shape "${el.shape}" is not a known frame`);
        if (!('image' in el) || !el.fit) errors.push(`${el.id}: frame needs image key and fit`);
      }
      if (el.type === 'image' && !el.src) errors.push(`${el.id}: image element needs src`);
      if (el.type === 'sticker' && !el.emoji) errors.push(`${el.id}: sticker needs emoji`);
    });
  });
};

/** Gate 2 — fidelity to the DesignSpec that produced the document. */
const validateSpecFidelity = (doc, spec, errors) => {
  const elements = doc.pages[0]?.elements || [];
  const byId = new Map(elements.map((el) => [el.id, el]));

  spec.objects
    .filter((obj) => obj.sectionId !== 'canvas') // background became page.background
    .forEach((obj) => {
      const el = byId.get(obj.id);
      if (!el) { errors.push(`spec object "${obj.id}" is missing from the document`); return; }
      if (el.type !== 'group' && (el.x !== obj.x || el.y !== obj.y || el.width !== obj.width || el.height !== obj.height)) {
        errors.push(`"${obj.id}": geometry drifted during compilation`);
      }
    });

  spec.groups.forEach((group) => {
    const el = byId.get(group.id);
    if (!el || el.type !== 'group') { errors.push(`spec group "${group.id}" is missing`); return; }
    group.memberIds.forEach((memberId) => {
      if (byId.get(memberId)?.parentId !== group.id) {
        errors.push(`"${memberId}" lost its membership of group "${group.id}"`);
      }
    });
  });

  const background = spec.objects.find((obj) => obj.sectionId === 'canvas');
  if (background && doc.pages[0].background !== background.shape.fill) {
    errors.push('page background does not match the spec background fill');
  }
};

/**
 * Validate a compiled editor document against the spec it came from.
 * Returns { valid, errors[] }.
 */
export const validateEditorDocument = (doc, spec) => {
  const errors = [];
  if (!doc || typeof doc !== 'object') return { valid: false, errors: ['document must be an object'] };
  validateEditorCompat(doc, errors);
  if (spec && errors.length === 0) validateSpecFidelity(doc, spec, errors);
  return { valid: errors.length === 0, errors };
};
